import Box from "@mui/material/Box";
import React, { useCallback, useMemo } from "react";
import {
  ACCOUNT_URL,
  DOMAIN_CONFIG_OBJ,
} from "../../../../../utils/axiosInstance";
import { useNavigate } from "react-router-dom";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../../../reduxStore/hooks";
import { QBOOK_DOMAIN_VALUES } from "../../../../../utils/constants/domainNameValues";
import {
  Avatar,
  CardHeader,
  Collapse,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Typography,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import HomeIcon from "@mui/icons-material/Home";
import BookIcon from "@mui/icons-material/Book";
import LiveHelpIcon from "@mui/icons-material/LiveHelp"; // figma icon is different using mui for easy use
import EditIcon from "@mui/icons-material/Edit";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import SettingsIcon from "@mui/icons-material/Settings";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { toggleTheme } from "../../../../../reduxStore/features/themeSlice";
import { signOutUser } from "../../../../../reduxStore/middleware/authMiddleware";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import LightModeIcon from "@mui/icons-material/LightMode";

import "./admin-nav.css";

type NavPropType = {
  showSideBar: boolean;
  attachSideBar?: boolean;
  setAttachSideBar?: any;
  isMobileView?: boolean;
};
type AdminNavChildrenType = {
  label: string;
  url?: string;
  onClickHandler?: (url?: string) => void;
  cyData?: string;
};
type AdminNavType = {
  label: string;
  icon: any;
  url?: string;
  showLabel?: boolean;
  children?: AdminNavChildrenType[];
  onClickHandler?: (url: string) => void;
  cyData?: string;
};
const adminNavValues = [
  {
    label: "Dashboard",
    icon: <HomeIcon sx={{ fontSize: "1rem" }} />,
    url: "/admin/dashboard",
    cyData: "admin-nav-dashboard",
  },
  {
    label: "Courses",
    icon: <BookIcon sx={{ fontSize: "1rem" }} />,
    url: "/admin/courses",
    cyData: "admin-nav-courses",
    // children: [
    //   {
    //     label: "List",
    //     url: "/admin/courses",
    //   },
    // ],
  },
  {
    label: "Question Bank",
    icon: <LiveHelpIcon sx={{ fontSize: "1rem" }} />,
    url: "/admin/questions",
    cyData: "admin-nav-question-bank",
  },
  {
    label: "Blogs",
    icon: <EditIcon sx={{ fontSize: "1rem" }} />,
    url: "/admin/blogs",
    cyData: "admin-nav-blogs",
    // children: [
    //   {
    //     label: "List",
    //     url: "/admin/blogs",
    //   },
    // ],
  },
];

const AdminNavItem = ({
  label,
  icon,
  url,
  children,
  onClickHandler,
  showLabel,
  cyData,
}: AdminNavType) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [open, setOpen] = React.useState(false);
  const { isDark } = useAppSelector((state) => state.theme);
  const isSelected = url && window.location.pathname.includes(url);
  return (
    <>
      <ListItemButton
        sx={{
          background: theme.palette.adminNavItem?.background1,
          borderRadius: "7px",
          color: isSelected
            ? theme.palette.adminNavItem?.text3
            : theme.palette.adminNavItem?.text1,
          pl: "5px",
          pr: showLabel ? "1em" : "5px",
          display: "flex",
          justifyContent: showLabel ? "space-between" : "center",
          border: theme.palette.adminNavItem?.border1
            ? `1px solid ${theme.palette.adminNavItem?.border1}`
            : "",
          boxShadow: "0px 4px 4px 0px rgba(0, 0, 0, 0.25)",
          fontSize: "14px !important",
        }}
        onClick={() => {
          if (children) {
            setOpen(!open);
            return;
          }
          url && onClickHandler && onClickHandler(url);
        }}
        cy-data={cyData}
      >
        {icon}
        {showLabel && (
          <ListItemText
            primary={label}
            sx={{ ml: "5px" }}
            primaryTypographyProps={{ fontSize: "14px" }}
          />
        )}
        {showLabel && children && children?.length > 0 && (
          <>
            {open ? (
              <KeyboardArrowDownIcon sx={{ fontSize: "1rem" }} />
            ) : (
              <KeyboardArrowRightIcon sx={{ fontSize: "1rem" }} />
            )}
          </>
        )}
      </ListItemButton>
      {showLabel && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "5px",
            }}
          >
            {children?.map((childItem, childIndex) => {
              return (
                <ListItemButton
                  key={`admin-nav-child-${childIndex}`}
                  onClick={() => {
                    childItem.url && navigate(childItem.url);
                    childItem.onClickHandler && childItem.onClickHandler();
                  }}
                  sx={{
                    background: theme.palette.adminNavItem?.background1,
                    pl: "1em",
                    borderRadius: "7px",
                    borderLeft: isDark
                      ? `7px solid ${theme.palette.adminNavItem?.border2}`
                      : "1px solid transparent",
                    color: theme.palette.adminNavItem?.text2,
                    boxShadow: "0px 4px 4px 0px rgba(0, 0, 0, 0.25)",
                  }}
                  cy-data={childItem.cyData}
                >
                  <ListItemText
                    primary={childItem.label}
                    primaryTypographyProps={{ fontSize: "14px" }}
                  />
                  {childItem.label?.includes("Switch theme") &&
                    (childItem.label.includes("Dark") ? (
                      <LightModeIcon sx={{ fontSize: "14px" }} />
                    ) : (
                      <DarkModeIcon sx={{ fontSize: "14px" }} />
                    ))}
                </ListItemButton>
              );
            })}
          </Box>
        </Collapse>
      )}
    </>
  );
};

const NavigationBar = ({
  showSideBar,
  attachSideBar,
  setAttachSideBar,
  isMobileView,
}: NavPropType) => {
  const { isDark } = useAppSelector((state) => state.theme);
  const { userData } = useAppSelector((state) => state.auth);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const theme = useTheme();

  // Memoize the change handler
  const handleChange = useCallback(
    (newValue: string) => {
      if (isMobileView) {
        setAttachSideBar(false);
      }
      navigate(newValue);
    },
    [isMobileView, navigate, setAttachSideBar]
  );

  const QbookLogo = useMemo(
    () => (
      <div
        className={
          !showSideBar
            ? ""
            : DOMAIN_CONFIG_OBJ.domainName.includes(QBOOK_DOMAIN_VALUES.quera)
            ? "admin-navigation-logo-container"
            : "navigation-logo-container"
        }
      >
        <img
          src={
            DOMAIN_CONFIG_OBJ.domainName === QBOOK_DOMAIN_VALUES.quera
              ? isDark
                ? DOMAIN_CONFIG_OBJ.darkThemeLogo
                : DOMAIN_CONFIG_OBJ.lightThemeLogo
              : DOMAIN_CONFIG_OBJ.simpleLogo
          }
          className={
            DOMAIN_CONFIG_OBJ.domainName !== QBOOK_DOMAIN_VALUES.quera
              ? "navigation-logo navigation-logo-large"
              : "navigation-logo-quera"
          }
          alt="navigation"
          cy-data="admin-navigation-logo"
        />
        {showSideBar &&
          DOMAIN_CONFIG_OBJ.domainName !== QBOOK_DOMAIN_VALUES.quera && (
            <span className="navigation-title navigation-title-large">
              {DOMAIN_CONFIG_OBJ.domainName}
            </span>
          )}
      </div>
    ),
    [isDark, showSideBar]
  );

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleThemeChange = useCallback(() => {
    const newTheme = isDark ? "light" : "dark";
    localStorage.setItem("qbookTheme", newTheme);
    document.querySelector("html")?.toggleAttribute("data-dark-mode");
    dispatch(toggleTheme(!isDark));
  }, [isDark, dispatch]);

  const signOutHandler = useCallback(() => {
    dispatch(
      signOutUser({ avoidClearingCookies: false, logoutRedirect: true })
    );
  }, [dispatch]);

  const adminNavValues2 = useMemo(
    () => [
      {
        label: "Settings",
        icon: <SettingsIcon sx={{ fontSize: "1rem" }} />,
        cyData: "admin-nav-settings",
        children: [
          {
            label: "Home",
            onClickHandler: () => navigate("/"),
            cyData: "admin-nav-settings-home",
          },
          {
            label: "Sign out",
            onClickHandler: () => signOutHandler(),
            cyData: "admin-nav-settings-sign-out",
          },
          {
            label: `Switch theme ( ${isDark ? "Dark" : "Light"} ) `,
            onClickHandler: () => handleThemeChange(),
            cyData: "admin-nav-settings-switch-theme",
          },
        ],
      },
    ],
    [navigate, signOutHandler, isDark, handleThemeChange]
  );

  const viewFullNav = useMemo(
    () => Boolean(showSideBar || attachSideBar),
    [attachSideBar, showSideBar]
  );

  const navBtn = useMemo(
    () => (
      <IconButton onClick={() => setAttachSideBar((pre: boolean) => !pre)}>
        <CloseIcon
          sx={{
            fontSize: "1em",
            color: isDark ? "#AEB9E1" : "inherit",
          }}
        />
      </IconButton>
    ),
    [setAttachSideBar, isDark]
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
      }}
      cy-data="qbook-admin-nav-bar"
    >
      <Box
        sx={{
          color: "black",
          display: "flex",
          flexDirection: "column",
          gap: "5px",
          width: "100%",
          px: isMobileView ? 0 : "1em",
        }}
      >
        <Box
          display="flex"
          justifyContent={isMobileView ? "flex-end" : "space-between"}
          width="100%"
        >
          {!isMobileView && QbookLogo}
          {isMobileView && viewFullNav && navBtn}
        </Box>
      </Box>
      <Box
        sx={{
          width: "100%",
          padding: "22px 1em 1em 1em",
        }}
      >
        <List
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            gap: "5px",
          }}
          component="nav"
        >
          {[...adminNavValues].map((tabValue: AdminNavType, i: number) => (
            <AdminNavItem
              key={`admin-nav-item${i}`}
              {...tabValue}
              onClickHandler={handleChange}
              showLabel={viewFullNav}
            />
          ))}
        </List>
      </Box>

      {isDark && <Divider flexItem sx={{ width: "100%" }} />}
      <Box
        sx={{
          width: "100%",
          padding: "1em",
        }}
      >
        <List
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            gap: "1em",
          }}
          component="nav"
        >
          {[...adminNavValues2].map((tabValue: AdminNavType, i: number) => (
            <AdminNavItem
              key={i}
              {...tabValue}
              onClickHandler={handleChange}
              showLabel={viewFullNav}
            />
          ))}
        </List>
        <CardHeader
          sx={{
            pr: "0",
            pl: "5px",
            "& .MuiCardHeader-avatar": {
              marginRight: viewFullNav ? "16px" : "0px !important",
            },
          }}
          avatar={
            <Avatar
              cy-data="qbook-admin-menu-user-avatar"
              alt="admin user avatar"
              src={userData?.personalInformation?.profilePhoto}
            >
              {userData?.firstName[0] || userData?.userName?.split(" ")[0][0]}
            </Avatar>
          }
          // action={
          //   viewFullNav && (
          //     <IconButton
          //       aria-label="settings"
          //       onClick={() => (window.location.href = ACCOUNT_URL)}
          //     >
          //       <KeyboardArrowRightIcon />
          //     </IconButton>
          //   )
          // }
          titleTypographyProps={{
            color: `${theme?.palette?.adminNav?.text1} !important`,
            maxWidth: "150px",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
          subheaderTypographyProps={{
            color: theme?.palette?.adminNav?.text2,
            maxWidth: "150px",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
          title={viewFullNav ? userData?.email : ""}
          subheader={
            viewFullNav ? (
              <Typography
                onClick={() => (window.location.href = ACCOUNT_URL)}
                sx={{
                  cursor: "pointer",
                  fontSize: "12px",
                  color: theme?.palette?.adminNav?.text2,
                  ":hover": {
                    textDecoration: "underline",
                  },
                }}
              >
                Account Settings
              </Typography>
            ) : (
              ""
            )
          }
        />
      </Box>
    </Box>
  );
};

export default React.memo(NavigationBar);
